import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { extractApiMessage } from "@/shared/api/extract-message";
import { toastError, toastExito } from "@/lib/toast";
import {
  atenderSolicitudCatalogo,
  contarSolicitudesCatalogoPendientes,
  eliminarSolicitudCatalogoAdmin,
  listarSolicitudesCatalogo,
  rechazarSolicitudCatalogo,
  type ListarSolicitudesCatalogoParams,
} from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";

/**
 * Bandeja PAGINADA server-side · estado/búsqueda/página forman parte de la key
 * (local · extiende qk.adminSolicitudesCatalogo para que los invalidate de las
 * mutations sigan alcanzando por prefijo).
 */
export const solicitudesCatalogoAdminQueryOptions = (
  params: ListarSolicitudesCatalogoParams = {},
) =>
  queryOptions({
    queryKey: [...qk.adminSolicitudesCatalogo, "paginado", params] as const,
    queryFn: () => listarSolicitudesCatalogo(params),
    placeholderData: keepPreviousData,
  });

export function useSolicitudesCatalogoAdmin(params: ListarSolicitudesCatalogoParams = {}) {
  return useQuery(solicitudesCatalogoAdminQueryOptions(params));
}

/**
 * Contador de pendientes · alimenta el badge del nav del administrador.
 * Polling suave para que las solicitudes nuevas "lleguen" sin refrescar.
 */
export function useSolicitudesCatalogoPendientes(enabled: boolean) {
  return useQuery({
    queryKey: qk.solicitudesCatalogoPendientes,
    queryFn: contarSolicitudesCatalogoPendientes,
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useResolverSolicitudCatalogo() {
  const qc = useQueryClient();
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: qk.adminSolicitudesCatalogo });
    void qc.invalidateQueries({ queryKey: qk.solicitudesCatalogoPendientes });
  };

  const atender = useMutation({
    mutationFn: ({ id, comentario }: { id: string; comentario?: string }) =>
      atenderSolicitudCatalogo(id, comentario),
    onSuccess: () => {
      invalidar();
      toastExito({ title: "Solicitud atendida", message: "Quedó marcada como atendida." });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo atender", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });

  const rechazar = useMutation({
    mutationFn: ({ id, comentario }: { id: string; comentario: string }) =>
      rechazarSolicitudCatalogo(id, comentario),
    onSuccess: () => {
      invalidar();
      toastExito({ title: "Solicitud rechazada", message: "El solicitante verá el motivo." });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo rechazar", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => eliminarSolicitudCatalogoAdmin(id),
    onSuccess: () => {
      invalidar();
      toastExito({
        title: "Solicitud eliminada",
        message: "Se quitó de tu bandeja · el solicitante conserva su notificación.",
      });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo eliminar", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });

  return { atender, rechazar, eliminar };
}
